/**
 * /video/:slug — dedicated single-video page.
 *
 * This is the canonical page for one specific video. It exists for two
 * flows:
 *   1. Internal: someone clicks a card on /insurance-education-videos
 *      → they navigate here. After watching they have the full site nav
 *      in the header (Home, Insurance Solutions, Leadership, Contact,
 *      Pay Premium, Account) so they can continue anywhere.
 *   2. External: someone pastes a URL like
 *      https://endurancecasualty.com/video/life-health-overview into
 *      email, SMS, or social, and the recipient lands here as a real
 *      page on the main site — with full header + footer.
 *
 * Layout: header → video player → title + description → CTA card →
 * back-to-library link → footer.
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import {
    ArrowLeft,
    ArrowRight,
    Phone,
    MessageSquareText,
    Loader2,
    AlertTriangle,
    Calendar,
} from "lucide-react";
import PageHeader from "@/components/PageHeader";
import FooterBar from "@/components/FooterBar";
import ContactDialog from "@/components/ContactDialog";
import { COMPANY } from "@/lib/compliance";

const API = "/api";

function buildStreamUrl(video) {
    if (!video) return null;
    if (video.public_url) {
        // If absolute, use as-is. If it starts with `/`, return it as a
        // same-origin relative URL — this avoids problems when the bundled
        // REACT_APP_BACKEND_URL drifts from the actual host the page was
        // loaded from (browsers handle cross-origin <video> Range requests
        // strictly and tend to abort even when CORS allows it).
        return video.public_url;
    }
    if (video.dropbox_path) {
        return `/api/videos/${video.id}/stream`;
    }
    return null;
}

function buildAbsoluteThumb(thumb) {
    if (!thumb) return undefined;
    if (thumb.startsWith("http")) return thumb;
    // Same-origin relative — robust against host drift.
    return thumb;
}

export default function VideoSharePage() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [video, setVideo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const videoRef = useRef(null);

    useEffect(() => {
        if (!slug) return;
        let cancelled = false;
        setLoading(true);
        setError(null);
        axios
            .get(`${API}/videos/${slug}`)
            .then((res) => {
                if (cancelled) return;
                setVideo(res.data);
            })
            .catch((e) => {
                if (cancelled) return;
                const code = e?.response?.status;
                setError(
                    code === 404
                        ? "This video does not exist or has been removed."
                        : "We could not load this video right now. Please try again shortly."
                );
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [slug]);

    const streamUrl = useMemo(() => buildStreamUrl(video), [video]);
    const thumbAbs = useMemo(
        () => buildAbsoluteThumb(video?.thumbnail_url),
        [video?.thumbnail_url]
    );
    const playable =
        !!streamUrl &&
        video?.status !== "coming_soon" &&
        video?.status !== "archived";

    // Per-page meta (document title + Open Graph) so when the URL is pasted
    // into iMessage / LinkedIn / Facebook the link unfurls into a preview
    // card with the right thumbnail and title.
    useEffect(() => {
        if (!video) return;
        document.title = `${video.title} — Endurance Casualty Insurance Agency`;
        const upsert = (selector, attrs) => {
            let el = document.head.querySelector(selector);
            if (!el) {
                el = document.createElement("meta");
                for (const [k, v] of Object.entries(attrs.create)) {
                    el.setAttribute(k, v);
                }
                document.head.appendChild(el);
            }
            for (const [k, v] of Object.entries(attrs.set)) {
                el.setAttribute(k, v);
            }
        };
        const pageUrl =
            typeof window !== "undefined" ? window.location.href : "";
        const desc =
            video.description ||
            "Insurance education from Endurance Casualty.";
        upsert('meta[name="description"]', {
            create: { name: "description" },
            set: { content: desc },
        });
        upsert('meta[property="og:title"]', {
            create: { property: "og:title" },
            set: { content: `${video.title} — Endurance Casualty` },
        });
        upsert('meta[property="og:description"]', {
            create: { property: "og:description" },
            set: { content: desc },
        });
        upsert('meta[property="og:type"]', {
            create: { property: "og:type" },
            set: { content: "video.other" },
        });
        upsert('meta[property="og:url"]', {
            create: { property: "og:url" },
            set: { content: pageUrl },
        });
        if (thumbAbs) {
            upsert('meta[property="og:image"]', {
                create: { property: "og:image" },
                set: { content: thumbAbs },
            });
        }
        upsert('meta[name="twitter:card"]', {
            create: { name: "twitter:card" },
            set: { content: "summary_large_image" },
        });
        upsert('meta[name="twitter:title"]', {
            create: { name: "twitter:title" },
            set: { content: `${video.title} — Endurance Casualty` },
        });
        upsert('meta[name="twitter:description"]', {
            create: { name: "twitter:description" },
            set: { content: desc },
        });
        if (thumbAbs) {
            upsert('meta[name="twitter:image"]', {
                create: { name: "twitter:image" },
                set: { content: thumbAbs },
            });
        }
    }, [video, thumbAbs]);

    // VideoObject JSON-LD — enables video thumbnails in Google search
    // results and makes the page eligible for video rich-result cards.
    // Injected into <head> with a stable id so we can clean it up when the
    // user navigates away.
    useEffect(() => {
        const NODE_ID = "ldjson-video-object";
        if (!video) {
            const old = document.getElementById(NODE_ID);
            if (old) old.remove();
            return;
        }
        const origin =
            typeof window !== "undefined" ? window.location.origin : "";
        const pageUrl =
            typeof window !== "undefined" ? window.location.href : "";
        const toAbsolute = (rel) => {
            if (!rel) return undefined;
            if (rel.startsWith("http")) return rel;
            return `${origin}${rel}`;
        };
        const contentUrl = toAbsolute(streamUrl);
        const thumbnailUrl = toAbsolute(thumbAbs);
        const schema = {
            "@context": "https://schema.org",
            "@type": "VideoObject",
            name: video.title,
            description:
                video.description ||
                "Insurance education from Endurance Casualty.",
            uploadDate: video.created_at,
            ...(video.updated_at ? { dateModified: video.updated_at } : {}),
            ...(thumbnailUrl ? { thumbnailUrl: [thumbnailUrl] } : {}),
            ...(contentUrl ? { contentUrl } : {}),
            embedUrl: pageUrl,
            ...(video.duration_seconds
                ? { duration: `PT${Math.round(video.duration_seconds)}S` }
                : {}),
            publisher: {
                "@type": "Organization",
                name: "Endurance Casualty Insurance Agency",
                url: "https://endurancecasualty.com/",
            },
        };
        let node = document.getElementById(NODE_ID);
        if (!node) {
            node = document.createElement("script");
            node.type = "application/ld+json";
            node.id = NODE_ID;
            document.head.appendChild(node);
        }
        node.textContent = JSON.stringify(schema);
        return () => {
            const cleanup = document.getElementById(NODE_ID);
            if (cleanup) cleanup.remove();
        };
    }, [video, streamUrl, thumbAbs]);

    const phoneDigits = (COMPANY.phone || "").replace(/[^\d]/g, "");

    return (
        <main
            data-testid="video-share-page"
            className="flex min-h-screen flex-col bg-brand-bg text-brand-ivory"
        >
            <PageHeader />
            <section className="flex-1 px-5 py-12 sm:px-8">
                <div className="mx-auto max-w-4xl">
                    <Link
                        to="/insurance-education-videos"
                        data-testid="video-share-back-link"
                        className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-brand-gold2 hover:text-brand-gold"
                    >
                        <ArrowLeft className="h-3.5 w-3.5" />
                        Back to Video Library
                    </Link>

                    {loading ? (
                        <div className="mt-10 flex items-center gap-3 rounded-2xl border border-[rgba(212,175,55,0.18)] bg-[#0b0e13] p-8 text-sm text-brand-muted">
                            <Loader2 className="h-4 w-4 animate-spin text-brand-gold" />
                            Loading…
                        </div>
                    ) : error ? (
                        <div
                            data-testid="video-share-error"
                            className="mt-10 rounded-2xl border border-rose-500/30 bg-rose-500/10 p-8 text-rose-200"
                        >
                            <div className="flex items-start gap-3">
                                <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
                                <div>
                                    <p className="font-semibold">
                                        We couldn&apos;t open this video
                                    </p>
                                    <p className="mt-1 text-sm opacity-90">
                                        {error}
                                    </p>
                                    <button
                                        type="button"
                                        onClick={() =>
                                            navigate(
                                                "/insurance-education-videos"
                                            )
                                        }
                                        className="mt-4 inline-flex items-center gap-2 rounded-full border border-rose-300/50 px-4 py-2 text-xs uppercase tracking-[0.2em] hover:bg-rose-500/20"
                                    >
                                        Browse the full library
                                        <ArrowRight className="h-3.5 w-3.5" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    ) : video ? (
                        <article className="mt-8">
                            <p className="text-[10px] uppercase tracking-[0.4em] text-brand-goldSoft">
                                {video.category_label ||
                                    "Insurance Education"}
                            </p>
                            <h1
                                data-testid="video-share-title"
                                className="mt-2 font-serif text-3xl text-brand-ivory sm:text-4xl lg:text-5xl"
                            >
                                {video.title}
                            </h1>

                            <div
                                data-testid="video-share-player-wrapper"
                                className="mt-6 overflow-hidden rounded-2xl border border-[rgba(212,175,55,0.25)] bg-black shadow-[0_30px_60px_rgba(0,0,0,0.5)]"
                            >
                                {playable ? (
                                    <video
                                        ref={videoRef}
                                        data-testid="video-share-player"
                                        controls
                                        preload="metadata"
                                        playsInline
                                        poster={thumbAbs}
                                        src={streamUrl}
                                        className="aspect-video w-full bg-black"
                                    >
                                        Your browser does not support HTML5
                                        video.
                                    </video>
                                ) : (
                                    <div
                                        className="flex aspect-video w-full flex-col items-center justify-center gap-3 bg-cover bg-center px-6 text-center"
                                        style={
                                            thumbAbs
                                                ? {
                                                      backgroundImage: `linear-gradient(to bottom, rgba(0,0,0,0.55), rgba(0,0,0,0.85)), url(${thumbAbs})`,
                                                  }
                                                : { backgroundColor: "#05070a" }
                                        }
                                    >
                                        <p className="text-[11px] uppercase tracking-[0.3em] text-brand-goldSoft">
                                            Coming soon
                                        </p>
                                        <p className="max-w-md text-sm text-brand-ivory/80">
                                            This video is being finalized. In
                                            the meantime, our licensed agents
                                            are happy to walk you through the
                                            topic by phone.
                                        </p>
                                    </div>
                                )}
                            </div>

                            {video.description ? (
                                <p
                                    data-testid="video-share-description"
                                    className="mt-6 max-w-3xl text-base leading-relaxed text-brand-muted sm:text-lg"
                                >
                                    {video.description}
                                </p>
                            ) : null}

                            <div className="mt-10 rounded-2xl border border-[rgba(212,175,55,0.25)] bg-[#0b0e13] p-6 sm:p-8">
                                <p className="text-[10px] uppercase tracking-[0.3em] text-brand-goldSoft">
                                    Questions about this coverage?
                                </p>
                                <h2 className="mt-2 font-serif text-2xl text-brand-ivory sm:text-3xl">
                                    Talk to a licensed Endurance agent today.
                                </h2>
                                <p className="mt-3 text-sm text-brand-muted sm:text-base">
                                    No obligation. Real coverage guidance from
                                    licensed humans — by phone, text, or a
                                    quick online consultation.
                                </p>
                                <div className="mt-6 flex flex-wrap gap-3">
                                    <ContactDialog
                                        defaultMessage={`I'd like to schedule a free consultation about: ${video.title}.`}
                                        trigger={
                                            <button
                                                type="button"
                                                data-testid="video-share-cta-consultation"
                                                className="inline-flex items-center gap-2 rounded-full border border-brand-gold bg-brand-gold px-6 py-3 text-xs font-semibold uppercase tracking-[0.25em] text-brand-bg transition-colors hover:bg-brand-gold2 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold focus-visible:ring-offset-2 focus-visible:ring-offset-brand-bg"
                                            >
                                                <Calendar className="h-4 w-4" />
                                                Schedule a free consultation
                                            </button>
                                        }
                                    />
                                    <a
                                        href={`tel:${phoneDigits}`}
                                        data-testid="video-share-cta-call"
                                        className="inline-flex items-center gap-2 rounded-full border border-brand-gold/50 px-6 py-3 text-xs uppercase tracking-[0.25em] text-brand-gold2 transition-colors hover:bg-brand-gold/10 hover:text-brand-gold focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold focus-visible:ring-offset-2 focus-visible:ring-offset-brand-bg"
                                    >
                                        <Phone className="h-4 w-4" />
                                        Call {COMPANY.phone}
                                    </a>
                                    <ContactDialog
                                        defaultMessage={`I'd like to send a message about: ${video.title}.`}
                                        trigger={
                                            <button
                                                type="button"
                                                data-testid="video-share-cta-message"
                                                className="inline-flex items-center gap-2 rounded-full border border-white/15 px-6 py-3 text-xs uppercase tracking-[0.25em] text-brand-muted transition-colors hover:border-brand-gold/40 hover:text-brand-gold2"
                                            >
                                                <MessageSquareText className="h-4 w-4" />
                                                Send a message
                                            </button>
                                        }
                                    />
                                </div>
                            </div>

                            <div className="mt-10 text-center">
                                <Link
                                    to="/insurance-education-videos"
                                    data-testid="video-share-library-link"
                                    className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-brand-gold2 hover:text-brand-gold"
                                >
                                    Watch more videos in the library
                                    <ArrowRight className="h-3.5 w-3.5" />
                                </Link>
                            </div>
                        </article>
                    ) : null}
                </div>
            </section>
            <FooterBar />
        </main>
    );
}
